import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { Chip } from '@shared/ui/atoms/Chip';
import Colours from '@shared/ui/atoms/Colours';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';
import { UserInfo } from '@shared/models/user.models';
import { UserTag } from '@shared/models/tags.models';
import { DialogIdentifiers, openDialogWithPayload } from '@shared/store/dialog.duck';
import { loadAllUserTags, selectAllUserTags } from '@features/settings/store/userManagement.duck';
import { fetchTags } from '@shared/store/userTags.duck';

interface Props {
  editingUser: UserInfo;
}

export function UserWorkGroupsSection(props: Props) {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const allUserTags = useSelector(selectAllUserTags);

  useEffect(() => {
    dispatch(loadAllUserTags());
    dispatch(fetchTags());
  }, [dispatch]);

  const memberships = useMemo(
    () => allUserTags.filter((userTag: UserTag) => userTag.userId === props.editingUser.userId),
    [allUserTags, props.editingUser]
  );

  const openAddToWorkGroups = () => {
    dispatch(openDialogWithPayload({
      activeDialog: DialogIdentifiers.AddUserToWorkGroupsDialog,
      payload: {
        user: props.editingUser,
        currentWorkGroupIds: memberships.map((userTag: UserTag) => userTag.tagId),
      },
    }));
  }

  return (
    <>
      <BodySmall colour={Colours.darkGrey} weight={600} style={{marginBottom: 8}}>{t('settings-page.edit-user.member-of-workgroups')}</BodySmall>
      {memberships.length > 0 ?
        <ChipRow>
          {memberships.map((userTag: UserTag) => (
            <Chip key={userTag.tagId} label={userTag.label} onClick={() => {}} />
          ))}
        </ChipRow> :
        <BodyRegular style={{textAlign: 'center', marginBottom: 16}}>
          {t('settings-page.edit-user.not-assigned-to-workgroups')}
        </BodyRegular>}
      <ButtonRow>
        <OutlineButton size={ButtonSize.Medium}
                       text="manage-users-page.assign-to-workgroup"
                       click={openAddToWorkGroups} />
      </ButtonRow>
    </>
  );
}

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
`

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
  & > div {
    cursor: default;
  }
`
